/**
 * Where a carried token physically sits: `sessionStorage`, or memory when there is none.
 *
 * `sessionStorage` rather than `localStorage`, and the choice is the whole safety argument. It is
 * scoped to one tab: a reload keeps it, closing the tab drops it, and a second tab on the same
 * machine never sees it. That is exactly the lifetime a seat has, so a token cannot outlive the
 * session it belongs to and cannot be presented from a window its owner is not looking at.
 *
 * Every call is guarded. A browser with storage switched off, a private window that refuses to
 * write and a quota that is somehow full all throw from the same three methods, and none of them is
 * worth failing a join over: the fallback is an in-memory map, which behaves exactly as the token
 * did before it was persisted at all.
 *
 * The port is deliberately three string methods and no more. Nothing here knows what a token is,
 * which is what keeps [carriedToken](./carriedToken.ts) testable in Node with no DOM.
 */

import type { CarriedTokenStore } from '@/net/session/carriedToken';

/** The tab's own storage, or memory when the browser will not give any. */
export function tabTokenStore(): CarriedTokenStore {
  const storage = openTabStorage();
  if (storage === null) return memoryTokenStore();

  return {
    read: (key: string): string | null => {
      try {
        return storage.getItem(key);
      } catch {
        return null;
      }
    },
    write: (key: string, value: string): void => {
      try {
        storage.setItem(key, value);
      } catch {
        // A seat that cannot be written down is a seat given up, not a join that fails.
      }
    },
    remove: (key: string): void => {
      try {
        storage.removeItem(key);
      } catch {
        // Same again: forgetting is best effort, and the host frees the seat regardless.
      }
    },
  };
}

/** The substitute, and what a test drives. Loses everything a reload was meant to keep. */
export function memoryTokenStore(): CarriedTokenStore {
  const kept = new Map<string, string>();
  return {
    read: (key: string): string | null => kept.get(key) ?? null,
    write: (key: string, value: string): void => {
      kept.set(key, value);
    },
    remove: (key: string): void => {
      kept.delete(key);
    },
  };
}

/**
 * Reading the global is itself the risky part: some browsers throw on the property access rather
 * than on the first call, so it is probed once here instead of at every read.
 */
function openTabStorage(): Storage | null {
  try {
    // Present in a browser, absent in Node, and the `in` check is what tells the two apart before
    // the property is ever read.
    if (!('sessionStorage' in globalThis)) return null;
    return globalThis.sessionStorage;
  } catch {
    return null;
  }
}
